/**
 * @fileoverview English translations.
 * @package PlayItNow
 * @module apps/front/lib/i18n/locales/en
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

export const en = {
  login: {
    title: 'PlayItNow',
    subtitle: 'Sign in to your admin space',
    emailLabel: 'Email or username',
    emailPlaceholder: 'your@email.com or username',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••',
    submitButton: 'Sign in',
    submitting: 'Signing in...',
    forgotPassword: 'Forgot password?',
    noAdminRights: 'You do not have admin rights',
    errorGeneric: 'An error occurred during login',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
  },
  auth: {
    optional: 'optional',
  },
  home: {
    title: 'Vote for your favorite songs at live concerts!',
    wantToVote: 'I want to vote',
    wantToVoteDescription: 'Create a free account to vote at concerts',
    wantToManageBand: 'I manage my band',
    wantToManageBandDescription: 'Create an account to manage your events and songs',
    alreadyHaveAccount: 'Already have an account?',
  },
  landing: {
    languageLabel: 'Choose your language',
    heroTitle: 'Let the audience choose the next song from your setlist',
    heroSubtitle:
      'Create your setlist. Fans scan a QR code and vote for the next song in real time. A unique experience at every show.',
    ctaPrimary: 'Create my band account — free trial',
    whyTitle: 'Why PlayItNow?',
    benefit1Title: 'One QR code, the crowd votes',
    benefit1Desc: 'Your audience scans and votes for their favorite songs from their phone. More interactivity.',
    benefit2Title: 'Live ranking',
    benefit2Desc: 'Votes display in real time. You play the most requested tracks on stage.',
    benefit3Title: 'Simple for bands',
    benefit3Desc: 'Create events, manage your setlist, launch voting in one click. Pricing for artists.',
    twoWaysTitle: 'Two ways to experience PlayItNow',
    ctaBand: 'I am a band',
    ctaBandDesc: 'Create events, add your songs, let the audience vote live.',
    ctaFan: 'I am a spectator',
    ctaFanDesc: 'Join a concert via QR code and vote for the songs you want to hear.',
    alreadyAccount: 'Already have an account?',
    login: 'Sign in',
    learnMore: 'View events',
  },
  public: {
    login: {
      title: 'PlayItNow',
      subtitle: 'Vote for your favorite songs!',
      subtitle2: 'Winning tracks will be played live!',
      identifyTitle: 'Identify yourself',
      identifySubtitle: 'Vote to choose the next song of the concert!',
      emailLabel: 'Email or username',
      emailPlaceholder: 'your@email.com or username',
      passwordLabel: 'Password',
      passwordPlaceholder: '••••••••',
      submitButton: 'Sign in',
      submitting: 'Signing in...',
      loading: 'Loading...',
      withoutRegistration: 'Access voting',
      or: 'OR',
      errorGeneric: 'An error occurred',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      firstNameLabel: 'Your first name (optional)',
      firstNamePlaceholder: 'John',
      firstNameHint: 'Your first name will be displayed when you vote',
      haveAccount: 'I have an account',
      forgotPassword: 'Forgot password?',
      createAccount: 'Create an account',
      pinDataHub: 'Manage my band',
      orDivider: 'or',
      loginWithGoogle: 'Sign in with Google',
      loginWithFacebook: 'Sign in with Facebook',
      oauthNoAccount: 'No account linked to this profile. Choose « Fan account » or « Band account » on the home page, then continue with Google or Facebook.',
      oauthFailed: 'Sign-in with this provider failed. Please try again.',
      oauthNoEmail: "Facebook did not provide an email address. Check app permissions or use an account with email, or sign up with Google.",
      oauthUnavailable: 'Sign-in with this provider is temporarily unavailable.',
      oauthUseProvider: 'Use Google or Facebook to sign in.',
      oauthCompleteEmailTitle: 'Facebook did not share your email',
      oauthCompleteEmailDescription: 'Enter your email address to complete sign-in.',
      oauthCompleteEmailSubmit: 'Complete sign-in',
      oauthCompleteEmailPlaceholder: 'your@email.com',
      oauthCompleteEmailExpired: 'Session expired. Try signing in with Facebook again.',
      oauthCompleteEmailNoAccount: 'Choose an account type (fan or band) from the home page first.',
    },
    register: {
      title: 'Create an account',
      subtitle: 'Join the PlayItNow community',
      firstNameLabel: 'First name',
      firstNamePlaceholder: 'John',
      lastNameLabel: 'Last name',
      lastNamePlaceholder: 'Doe',
      emailLabel: 'Email',
      emailPlaceholder: 'your@email.com',
      usernameLabel: 'Username',
      usernamePlaceholder: 'john.doe',
      autoGenerated: 'Auto-generated',
      usernameHint: 'You can modify this username',
      submitButton: 'Create my account',
      submitting: 'Creating account...',
      backToLogin: 'Back to login',
      orDivider: 'or',
      continueWithGoogle: 'Continue with Google',
      continueWithFacebook: 'Continue with Facebook',
      successTitle: 'Registration successful!',
      successMessage:
        'A confirmation email has been sent. Check your inbox to activate your account.',
      error: {
        firstNameRequired: 'First name is required',
        lastNameRequired: 'Last name is required',
        emailRequired: 'Email is required',
        generic: 'An error occurred while creating the account',
      },
      // Band registration fields
      bandNameLabel: 'Band name',
      bandNamePlaceholder: 'Your band name',
      passwordLabel: 'Password',
      passwordPlaceholder: '••••••••',
      passwordMinLength: 'Password must be at least 8 characters',
      logoUrlLabel: 'Logo URL (optional)',
      logoUrlPlaceholder: 'https://example.com/logo.png',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
    },
    events: {
      title: 'Upcoming Events',
      noEvents: 'No upcoming events',
      noEventsDescription: 'There are no events scheduled in the next 2 days.',
      errorLoad: 'Error loading events',
      settings: 'Settings',
      logout: 'Logout',
    },
    event: {
      invalidSlug: 'Invalid event slug',
      notFound: 'Event not found',
      errorLoad: 'Error loading event',
      backToEvents: 'Back to events',
      songsTitle: 'Available songs',
      noSongs: 'No songs available at the moment',
      vote: 'Vote',
      voted: 'Voted',
      loading: 'Loading...',
      votesRemaining: 'Votes remaining',
      voteSuccess: 'Your vote has been registered!',
      voteError: 'Error registering your vote',
      totalVotes: 'Total votes',
      votesLabel: 'votes',
      blindVoteMessage: 'The ranking will be revealed by the band.',
      rankingTitle: 'Ranking',
      votingPaused: 'Voting is currently suspended.',
      votingCompleted: 'This event is completed. Voting is closed.',
      settings: 'Settings',
      logout: 'Logout',
    },
    qrCode: {
      title: 'Share this event',
      description: 'Scan this QR code or share the link to invite others to vote',
      share: 'Share',
      copy: 'Copy link',
      show: 'Show QR code',
      shareText: 'Join me to vote on songs for {{eventName}}!',
    },
    settings: {
      title: 'Account Settings',
      account: 'Account',
      email: 'Email',
      username: 'Username',
      firstName: 'First name',
      lastName: 'Last name',
      save: 'Save',
      saving: 'Saving...',
      success: 'Settings saved successfully',
      error: 'Error saving settings',
      password: {
        title: 'Change password',
        currentPassword: 'Current password',
        currentPasswordPlaceholder: '••••••••',
        newPassword: 'New password',
        newPasswordPlaceholder: '••••••••',
        confirmPassword: 'Confirm new password',
        confirmPasswordPlaceholder: '••••••••',
        change: 'Change password',
        changing: 'Changing...',
        success: 'Password changed successfully. Please log in again.',
        error: 'Error changing password',
        mismatch: 'Passwords do not match',
        incorrect: 'Current password is incorrect',
      },
      deleteAccount: {
        title: 'Delete account',
        description: 'Deleting your account is permanent and cannot be undone.',
        button: 'Delete my account',
        confirming: 'Confirming...',
        confirmTitle: 'Confirm deletion',
        confirmMessage:
          'Are you sure you want to delete your account? This action is irreversible.',
        confirmButton: 'Yes, delete',
        cancelButton: 'Cancel',
        success: 'Account deleted successfully',
        error: 'Error deleting account',
      },
    },
    dataExport: {
      title: 'Export Your Data',
      description:
        'In accordance with GDPR, you can request the export of all your personal data stored on PlayItNow.',
      whatIncluded: 'The export will include:',
      included: {
        profile: 'Your profile information (email, name, first name, etc.)',
        votes: 'Your voting history',
        preferences: 'Your preferences (theme, language)',
        sessions: 'Your login sessions',
      },
      exportButton: 'Export my data',
      exporting: 'Exporting...',
      success: 'Your data has been successfully exported. Download will begin.',
      error: 'An error occurred while exporting your data.',
      note: 'Note: Exporting your data may take a few moments. The file will be in JSON format.',
    },
  },
  legal: {
    title: 'Legal Notice',
    editor: {
      title: 'Publisher',
      name: 'axialdata',
      email: 'Email: contact@axialdata.net',
      siret: 'SIRET: 49347625300023',
    },
    publication: {
      title: 'Publication Director',
      director: 'Pierre-Yves Léglise',
    },
    hosting: {
      title: 'Hosting',
      name: 'Hostinger International Ltd',
      addressLine1: 'The application is hosted in France (Paris data center).',
      addressLine2: 'Registered office: Švitrigailos g. 34, LT-03230 Vilnius, Lithuania',
      country: 'Lithuania',
      url: 'https://www.hostinger.com',
    },
    dataProtection: {
      title: 'Data Protection',
      description:
        'In accordance with the General Data Protection Regulation (GDPR), you have rights regarding your personal data.',
      link: 'View our privacy policy',
    },
  },
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: October 2025',
    intro: {
      title: 'Introduction',
      description:
        'PlayItNow is committed to protecting your privacy and personal data. This policy explains how we collect, use, and protect your information.',
    },
    dataCollection: {
      title: 'Data Collected',
      bands: {
        title: 'For Bands (Paying Customers)',
        email: 'Email address',
        username: 'Username',
        name: 'Band name and contact information',
        payment: 'Payment information (via secure third-party provider)',
        logo: 'Band logo',
      },
      spectators: {
        title: 'For Spectators (Free Users)',
        email: 'Email address (if account creation)',
        firstName: 'First name (optional, for display during votes)',
        fingerprint: 'Browser fingerprint (anti-fraud)',
        ip: 'IP address (for security and audit)',
        votes: 'Voting history',
      },
    },
    purposes: {
      title: 'Purposes of Processing',
      authentication: 'User authentication and account management',
      voting: 'Interactive voting system management',
      fraud: 'Fraud prevention and vote security',
      billing: 'Billing and subscription management (bands only)',
      support: 'Customer support and technical assistance',
      preferences: 'User preference storage (theme, language)',
      analytics: 'Site audience measurement (self-hosted Rybbit, internal use only: page views, referrer; no data sold or transferred)',
    },
    legalBasis: {
      title: 'Legal Basis',
      description:
        'The processing of your data is based on: (1) contract performance for bands, (2) your consent for spectators, (3) our legitimate interest for security and fraud prevention.',
    },
    retention: {
      title: 'Data Retention',
      active: 'Active account data: retained for the duration of service use',
      inactive: 'Inactive account data: deleted after 3 years of inactivity',
      legal: 'Data required for legal obligations: retained according to applicable legal periods',
    },
    rights: {
      title: 'Your Rights',
      description: 'In accordance with GDPR, you have the following rights:',
      access: 'Right of access: view your personal data',
      rectification: 'Right of rectification: correct your inaccurate data',
      deletion: 'Right to erasure: delete your data (right to be forgotten)',
      portability: 'Right to portability: retrieve your data in a structured format',
      opposition: 'Right to object: object to the processing of your data',
      limitation: 'Right to restriction: limit the processing of your data',
      contact:
        'To exercise your rights, contact us at: contact@axialdata.net. You can also export or delete your data from Account settings.',
    },
    security: {
      title: 'Data Security',
      description:
        'We implement appropriate technical and organizational measures to protect your data against unauthorized access, loss, destruction, or alteration. Data is stored on secure servers and communications are encrypted.',
    },
    cookies: {
      title: 'Cookies and Trackers',
      description:
        'Our application uses essential cookies for service operation. For more information, see our cookie policy.',
      link: 'Cookie Policy',
    },
    contact: {
      title: 'Contact',
      description: 'For any questions regarding this policy or your personal data:',
      email: 'contact@axialdata.net',
    },
  },
  cookies: {
    title: 'Cookie Policy',
    intro: {
      title: 'Introduction',
      description:
        'This page informs you about the use of cookies and similar technologies on PlayItNow.',
    },
    essential: {
      title: 'Essential Cookies',
      description:
        'The following cookies are strictly necessary for service operation and do not require prior consent:',
      accessToken: {
        title: 'access_token',
        description:
          'Authentication cookie that maintains your logged-in session. This cookie is httpOnly (not accessible via JavaScript) and secure.',
        duration: 'Duration: 15 minutes',
      },
      refreshToken: {
        title: 'refresh_token',
        description:
          'Cookie that automatically renews your session. This cookie is httpOnly and secure.',
        duration: 'Duration: 7 days',
      },
      csrfToken: {
        title: 'csrf_token',
        description:
          'CSRF (Cross-Site Request Forgery) protection cookie. This cookie is accessible via JavaScript for client-side validation.',
        duration: 'Duration: 7 days',
      },
    },
    localStorage: {
      title: 'Local Storage (localStorage)',
      description: "We also use your browser's local storage to remember your preferences:",
      theme: 'Display theme (light/dark mode)',
      lang: 'Preferred language',
      firstname: 'First name (for display during votes)',
      fingerprint: 'Browser fingerprint (anti-fraud)',
    },
    analytics: {
      title: 'Audience measurement (Rybbit)',
      description:
        'When audience measurement is enabled (production), a Rybbit script, hosted on our own servers (analytics.axialdata.app), sends minimal data (page views, referrer) to analyze site usage, for internal use only. No advertising; no data is sold or transferred to third parties. This processing is based on our legitimate interest. Rybbit may use a technical identifier (cookie or local storage) to deduplicate visits. You can block third-party scripts in your browser to disable this tracking.',
    },
    management: {
      title: 'Cookie Management',
      description:
        'You can manage cookies through your browser settings. Note that disabling essential cookies will prevent the application from functioning properly.',
    },
  },
  terms: {
    title: 'Terms of Service',
    lastUpdated: 'Last updated: October 2025',
    intro: {
      title: 'Article 1 - Object',
      description:
        'These Terms of Service (TOS) govern the use of the PlayItNow service, a SaaS platform allowing music bands to offer an interactive experience to their audience during concerts. These TOS apply to both bands (paying customers) and spectators (free users).',
    },
    service: {
      title: 'Article 2 - Service Description',
      description:
        'PlayItNow is a web application allowing music bands to create events, manage a song library, and enable the public to vote for songs they want to hear. The service is paid for bands and free for spectators.',
    },
    forBands: {
      title: 'Article 3 - Conditions Specific to Bands',
      subtitle:
        'The following conditions apply only to bands using the service as paying customers:',
      registration: {
        title: '3.1 - Registration and Account',
        description:
          'To use the service as a band, you must create an account by providing accurate and up-to-date information. You are responsible for the confidentiality of your credentials and all activities performed from your account.',
      },
      subscription: {
        title: '3.2 - Subscriptions and Pricing',
        description:
          'Access to the service for bands is subject to a paid subscription. Prices, plans, and subscription conditions are available on the platform. Subscriptions are automatically renewed unless you cancel.',
      },
      payment: {
        title: '3.3 - Payment',
        description:
          'Payments are made through a secure payment provider. In case of payment default, access to the service may be suspended. No refund is due in case of early termination, except as required by law.',
      },
      obligations: {
        title: '3.4 - Obligations',
        customer: {
          title: '3.4.1 - Customer Obligations',
          accuracy: 'Provide accurate and up-to-date information',
          security: 'Maintain confidentiality of your credentials',
          compliance: 'Comply with applicable laws and regulations',
          prohibited: 'Do not use the service for illegal or fraudulent purposes',
        },
        provider: {
          title: '3.4.2 - Provider Obligations',
          availability: 'Ensure service availability as much as possible',
          support: 'Provide technical support within service limits',
          security: 'Implement appropriate security measures',
        },
      },
      intellectualProperty: {
        title: '3.5 - Intellectual Property',
        description:
          'All elements of the PlayItNow service (code, design, trademarks, etc.) are the exclusive property of axialdata. Use of the service does not grant any ownership rights to these elements.',
      },
      termination: {
        title: '3.6 - Termination',
        description:
          'You can cancel your subscription at any time from your customer area. axialdata reserves the right to suspend or terminate any account in case of non-compliance with these TOS.',
      },
      jurisdiction: {
        title: '3.7 - Applicable Law and Jurisdiction',
        description:
          'These TOS are governed by French law. In case of dispute, French courts have exclusive jurisdiction.',
      },
    },
    forSpectators: {
      title: 'Article 4 - Conditions Specific to Spectators',
      subtitle: 'The following conditions apply only to spectators using the service for free:',
      voting: {
        title: '4.1 - Voting System',
        description: 'When participating in an event, you can:',
        rules: {
          one: 'Vote for one or more songs according to rules set by the band',
          fraud: 'Respect voting limits to avoid fraud',
          respect: 'Use the service respectfully and in accordance with its purpose',
        },
      },
      account: {
        title: '4.2 - User Account',
        description:
          'Creating an account is optional. You can participate anonymously by providing only a first name. If you create an account, you are responsible for the confidentiality of your credentials.',
      },
      conduct: {
        title: '4.3 - Code of Conduct',
        description: 'It is strictly prohibited to:',
        prohibited: {
          fraud: 'Attempt to defraud the voting system',
          abuse: 'Use the service abusively or maliciously',
          illegal: 'Use the service for illegal purposes',
        },
      },
      privacy: {
        title: '4.4 - Personal Data',
        description:
          'The processing of your personal data is governed by our privacy policy. By using the service, you accept this policy.',
        link: 'View our privacy policy',
      },
    },
    liability: {
      title: 'Article 5 - Liability',
      description:
        'axialdata shall not be liable for direct or indirect damages resulting from the use or inability to use the service. For bands, liability is limited to the amount of sums paid for the subscription. For spectators, the service is provided "as is" without any warranty.',
    },
    modifications: {
      title: 'Article 6 - Modifications',
      description:
        'axialdata reserves the right to modify these TOS at any time. Modifications take effect upon publication. Your continued use of the service constitutes acceptance of the new conditions.',
    },
    contact: {
      title: 'Article 7 - Contact',
      description: 'For any questions regarding these TOS:',
      email: 'contact@axialdata.net',
    },
  },
  cookieBanner: {
    message:
      'This site uses essential cookies and audience measurement (anonymous, strictly internal use). By continuing to browse, you accept their use.',
    learnMore: 'Learn more about cookies',
    accept: 'I accept',
  },
  footer: {
    legal: 'Legal Notice',
    privacy: 'Privacy',
    cookies: 'Cookies',
    terms: 'Terms',
  },
  errors: {
    EVENT_NOT_AVAILABLE_YET:
      'This event is not available yet. Come back at the scheduled date and time to access voting.',
    networkError:
      'Unable to reach the server. Check your connection or try again later.',
    unexpectedError: 'An unexpected error occurred. Please try again later.',
    validationError: 'Invalid data. Please check the form.',
    badRequest: 'Invalid request.',
    unauthorized: 'Authentication required.',
    forbidden: 'Access denied.',
    notFound: 'Resource not found.',
    invalidJsonBody: 'Invalid data format.',
    invalidCredentials: 'Invalid credentials.',
    emailAlreadyRegistered: 'This email address is already registered.',
    notAuthenticated: 'You must be logged in.',
    tokenRequired: 'Invalid or expired link.',
    ONE_SHOW_DATE_OUT_OF_RANGE:
      'ONE_SHOW event date must be between purchase date and +30 days',
  },
  common: {
    copyright: 'All rights reserved',
    year: new Date().getFullYear().toString(),
    company: 'axialdata',
    required: 'Required',
    back: 'Back',
    goHome: 'Go to home',
    close: 'Close',
    cancel: 'Cancel',
    selectLanguage: 'Select language',
    skipToContent: 'Skip to main content',
    activateDarkMode: 'Activate dark mode',
    activateLightMode: 'Activate light mode',
    loading: 'Loading...',
    processing: 'Processing...',
    logoUrlExpiringModalTitle: 'Image URL may expire',
    logoUrlExpiringModalMessage:
      'This URL points to a host (e.g. Facebook) whose image links can become invalid over time. The image may stop displaying later. You can still save this URL if you wish.',
    ok: 'OK',
  },
  pwa: {
    install: {
      button: 'Install app',
      installButton: 'Install',
      promptDescription: 'Install PlayItNow on your device for a better experience',
      waiting: 'Waiting for installation permission...',
      iosButton: 'Install on iOS',
      iosTitle: 'Install PlayItNow on your iPhone/iPad',
      iosStep1: '1. Tap the share button at the bottom of the screen',
      iosStep2: '2. Scroll down and select "Add to Home Screen"',
      iosStep3: '3. Tap "Add" to install the app',
    },
  },
  forgotPassword: {
    title: 'Forgot password',
    subtitle: 'Enter your email address to receive a reset link',
    emailLabel: 'Email address',
    emailPlaceholder: 'your@email.com',
    submitButton: 'Send reset link',
    submitting: 'Sending...',
    successMessage: 'If an account with that email exists, a password reset link has been sent.',
    backToLogin: 'Back to login',
    errorGeneric: 'An error occurred',
  },
  resetPassword: {
    title: 'Reset password',
    subtitle: 'Enter your new password',
    passwordLabel: 'New password',
    passwordPlaceholder: '••••••••',
    confirmPasswordLabel: 'Confirm password',
    confirmPasswordPlaceholder: '••••••••',
    submitButton: 'Reset password',
    submitting: 'Resetting...',
    successMessage: 'Your password has been reset successfully. You can now log in.',
    passwordsDoNotMatch: 'Passwords do not match',
    backToLogin: 'Back to login',
    errorGeneric: 'An error occurred during reset',
    invalidToken: 'The reset link is invalid or has expired',
  },
  confirmEmail: {
    title: 'Confirm your email address',
    setPasswordTitle: 'Set your password',
    setPasswordSubtitle: 'Choose a password to complete your registration.',
    passwordLabel: 'Password',
    confirmPasswordLabel: 'Confirm password',
    submitButton: 'Activate my account',
    confirming: 'Confirming…',
    successFan: 'Account activated. Redirecting…',
    successBand: 'Account created. Redirecting…',
    alreadyCompleted: 'Registration already completed or link expired. Please log in.',
    goToLogin: 'Go to login',
    invalidToken: 'Link invalid or expired. Please register again or request a new email.',
    missingToken: 'Confirmation link missing.',
    errorGeneric: 'An error occurred.',
    backToRegister: 'Back to registration',
  },
  admin: {
    nav: {
      dashboard: 'Dashboard',
      events: 'Events',
      songs: 'Songs',
      venues: 'Venues',
      members: 'Members',
      settings: 'Settings',
      userSettings: 'My account',
      logout: 'Logout',
    },
    dashboard: {
      welcome: 'Welcome',
      bandName: 'Band',
      superadminMode: 'Super Admin Mode',
      totalEvents: 'Total events',
      activeEvents: 'Active events',
      upcomingEvents: 'Upcoming events',
      quickActions: 'Quick actions',
      createEvent: 'Create event',
      manageVenues: 'Manage venues',
      recentEvents: 'Recent events',
      viewAll: 'View all',
      noEvents: 'No events yet',
      createFirstEvent: 'Create your first event',
      noVenue: 'No venue',
      errorLoad: 'Error loading data',
      status: {
        pending: 'Pending',
        active: 'Active',
        completed: 'Completed',
      },
    },
    planBadge: {
      tooltip: 'Current plan — Click to manage your subscription',
      ariaLabel: 'View and manage your subscription',
    },
    oneShowAlert: {
      title: 'OneShow event validity',
      daysRemaining: '{{days}} day(s) remaining',
      daysShort: '{{days}}d',
      daysRemainingMessage: 'You have {{days}} day(s) left to use this event in unlimited mode.',
      urgentTitle: 'Expiration imminent',
      expired: 'OneShow event expired',
      expiredShort: 'Expired',
      expiredMessage:
        'This event has reached the end of its validity period. Upgrade to ProGig for unlimited events.',
      upgradeCta: 'View plans',
    },
    subscription: {
      plan: {
        FREE: 'Free',
        PRO_GIG: 'ProGig',
        LIVE_TOUR: 'LiveTour',
        ONE_SHOW: 'OneShow',
      },
      title: 'Subscription',
      subtitle: 'Manage your band plan and usage limits',
      currentPlan: 'Current plan',
      currentPlanFree: 'Free (FREE)',
      currentPlanProGig: 'ProGig',
      currentPlanLiveTour: 'LiveTour',
      freeDescription: 'Perfect for discovering and trying the service.',
      proGigDescription: 'Plan for bands playing several gigs per month.',
      liveTourDescription: 'Plan for intensive touring and unlimited usage.',
      chooseProGig: 'Switch to ProGig',
      chooseLiveTour: 'Switch to LiveTour',
      loading: 'Loading your subscription...',
      errorLoad: 'An error occurred while loading your subscription.',
      errorCheckout:
        'Error while creating the Stripe payment session. Please try again in a few moments.',
      successTitle: 'Payment successful',
      successMessage: 'Your payment has been processed. Your subscription will be updated shortly.',
      successRedirect: 'You will be redirected to your dashboard shortly.',
      monthlyQuota: 'Monthly quota',
      freeQuota: 'Free quota',
      eventsUsed: '{{current}} / {{limit}} events',
      billingPeriod: 'Period: {{start}} - {{end}}',
      quotaReached: 'Monthly quota reached',
      quotaReachedMessage:
        'You have reached your event limit for this period. The quota will reset at the end of your billing period.',
      quotaWarning: 'Monthly quota almost reached',
      quotaWarningMessage: 'You have {{remaining}} event(s) remaining for this period.',
      quotaInfo: 'You have {{remaining}} event(s) remaining for this period.',
      upgradePlan: 'Upgrade plan',
      upgradeToUnlimited: 'Upgrade to unlimited',
      upgradeSingleEventConfirm: 'You are about to upgrade the event "{{eventName}}" to unlimited.',
      singleEventDescription:
        'This is a one-time payment for unlimited votes for this specific event.',
      oneShowValidity: 'Valid {{days}} days from purchase.',
      oneShowNonRefundable:
        'Non-refundable payment: once made, the payment cannot be cancelled or refunded.',
      multipleProGigSuggestion: 'Multiple events? The ProGig plan might be more cost-effective.',
      comparePlans: 'Compare plans →',
      upgradeToUnlimitedNow: 'Upgrade to unlimited now',
      checkoutError: 'Error creating payment session. Please try again.',
      oneShowPrice: 'Price',
      oneShowPaymentType: 'One-time payment',
      oneShowBenefitsTitle: 'What you get:',
      oneShowBenefitVotes: 'Unlimited votes for this event',
      oneShowBenefitTime: 'No active time limit',
      chooseOption: 'Choose an option',
      convertEvent: 'Convert an event',
      createEvent: 'Create an event',
      selectFreeEvent: 'Select a free event to convert:',
      convertThisEvent: 'Convert this event',
      noFreeEventsToConvert: 'No free events to convert. Create an event first.',
      createDescription: 'Create a new event with the One Show plan.',
      createNewOneShot: 'Create One Show event',
      createOneShotEventTitle: 'Create One Show event',
      proceedToPayment: 'Proceed to payment',
      oneShowDescription: 'One-time payment for an unlimited event.',
      configureOneShot: 'Configure',
      convertEventFromUrl: 'Event "{{name}}" will be converted to One Show event.',
      priceFree: 'Free',
      priceOneTime: '{{price}}€ (one-time payment)',
      pricePerMonth: '{{price}}€ / month',
      currentPlanBadge: 'Current plan',
      selectPlan: 'Choose',
      planFree: 'Free',
      featureMaxEvents: 'Maximum events',
      featureMaxVotes: 'Votes per event',
      featureActiveTime: 'Cumulative active time',
      featureEventsPerMonth: 'Events per month',
      featureOneEvent: '1 event (single)',
      featureValidity: 'Validity',
      validityDays: '{{days}} days',
      unlimited: 'Unlimited',
      events: 'events',
      votes: 'votes',
      minutes: 'min',
      planNote: {
        FREE: '5 events max (excluding OneShow). 8 votes per event. 40 min cumulative activation.',
        ONE_SHOW: 'One-time payment. One event unlimited (unlimited votes, no time limit).',
        PRO_GIG: '3 events per month (rolling period based on Stripe billing).',
        LIVE_TOUR: 'Unlimited events. Ideal for tours.',
      },
      cancelSubscriptionButton: 'Cancel subscription',
      cancelSubscriptionConfirmTitle: 'Cancel subscription',
      cancelSubscriptionConfirmMessage:
        'No refund. Your subscription remains active until {{date}} (end of the paid period), then you switch back to the free plan. Continue?',
      cancelSubscriptionSuccess: 'Subscription cancelled. You keep access until {{date}}.',
      cancelSubscriptionError: 'Error cancelling subscription.',
      accessUntil: 'Access until {{date}}',
      cancellationScheduled: 'Cancellation scheduled',
      cancelling: 'Cancelling...',
    },
    events: {
      title: 'Events',
      subtitle: 'Manage your concerts and events',
      eventsList: 'Events list',
      upcomingEvents: 'Upcoming events',
      pastEvents: 'Past events',
      noEvents: 'No events yet',
      addEvent: 'Create event',
      add: 'Create',
      adding: 'Creating...',
      edit: 'Edit',
      editing: 'Editing...',
      cancel: 'Cancel',
      confirm: 'Confirm',
      confirmDeleteTitle: 'Delete event',
      confirmDelete: 'Are you sure you want to delete the event "{{name}}"?',
      name: 'Event name',
      namePlaceholder: 'Ex: Concert at Zenith',
      venue: 'Venue',
      selectVenue: 'Select a venue',
      date: 'Date',
      time: 'Time',
      oneShowDateRestriction: 'Date locked: between purchase and +{{days}} days',
      maxVotesPerUser: 'Maximum votes per user',
      maxVotes: 'Max votes',
      tiebreaker: 'Tiebreaker',
      tiebreakerChronological: 'Chronological (first voted)',
      tiebreakerRandom: 'Random',
      tiebreakerManual: 'Manual (group choice)',
      showRanking: 'Show ranking',
      showRankingToPublic: 'Show ranking to public',
      blindVote: 'Blind vote',
      status: {
        pending: 'Pending',
        active: 'Active',
        completed: 'Completed',
      },
      planBadge: {
        subscription: 'Subscription (unlimited)',
        oneShow: 'OneShow (unlimited event)',
      },
      addSuccess: 'Event created successfully',
      addError: 'Error creating event',
      updateSuccess: 'Event updated successfully',
      updateError: 'Error updating event',
      deleteSuccess: 'Event deleted successfully',
      deleteError: 'Error deleting event',
      errorLoad: 'Error loading events',
      noVenues: 'You must create at least one venue before creating an event',
      qrCodeTitle: 'QR Code generated',
      qrCodeDescription: 'Use this code to allow spectators to vote',
      copyQRCode: 'Copy QR code',
      qrCodeCopied: 'QR code copied to clipboard',
      showQRCode: 'Show QR code',
      qrCodePrint: 'Print',
      qrCodeShare: 'Share',
      qrCodeDownload: 'Download',
      qrCodeUrl: 'Event URL',
      qrCodeShareText: 'Join me to vote on songs for {{eventName}}!',
      qrCodePrintTitle: 'QR Code - PlayItNow',
      qrCodePrintInstructions:
        'Scan this QR code to access the event and vote for your favorite songs. The winning songs will be played live!',
      delete: 'Delete',
      activate: 'Activate',
      deactivate: 'Deactivate',
      complete: 'Complete',
      reactivate: 'Reactivate',
      activateSuccess: 'Event activated successfully',
      activateError: 'Error activating event',
      deactivateSuccess: 'Event deactivated successfully',
      deactivateError: 'Error deactivating event',
      completeSuccess: 'Event completed successfully',
      completeError: 'Error completing event',
      showRankingBtn: 'Show ranking to spectators',
      hideRankingBtn: 'Hide ranking from spectators',
      rankingHidden: 'Ranking hidden from spectators',
      rankingVisible: 'Ranking visible to spectators',
      toggleBlindVoteError: 'Error changing ranking visibility',
      live: {
        title: 'Live Dashboard',
        loading: 'Loading...',
        errorLoad: 'Error loading data',
        errorToggle: 'Error changing visibility',
        errorMarkPlayed: 'Error marking song',
        eventNotFound: 'Event not found',
        backToEvents: 'Back to events',
        totalVotes: 'Total votes',
        uniqueVoters: 'Unique voters',
        songsPlayed: 'Songs played',
        noVotesYet: 'No votes yet',
        noVotes: 'No votes yet',
        rankingHidden: 'Ranking hidden',
        rankingVisible: 'Ranking visible',
        markPlayed: 'Mark as played',
        unmarkPlayed: 'Unmark as played',
        autoRefresh: 'Auto refresh',
        openLive: 'Open Live',
      },
      songs: {
        title: 'Event Songs',
        subtitle: 'Manage songs available for voting',
        addSong: 'Add song',
        searchExisting: 'Search existing song',
        searchPlaceholder: 'Search by title or artist...',
        noResults: 'No songs found',
        or: 'OR',
        songTitle: 'Title',
        titlePlaceholder: 'Ex: Bohemian Rhapsody',
        artist: 'Artist',
        artistPlaceholder: 'Ex: Queen',
        duration: 'Duration',
        durationPlaceholder: 'Ex: 5:55',
        year: 'Year',
        yearPlaceholder: 'Ex: 2020',
        add: 'Add',
        adding: 'Adding...',
        remove: 'Remove',
        noSongs: 'No songs for this event',
        addSuccess: 'Song added successfully',
        addError: 'Error adding song',
        createSuccess: 'Song created and added successfully',
        createError: 'Error creating song',
        removeSuccess: 'Song removed successfully',
        removeError: 'Error removing song',
        errorLoad: 'Error loading songs',
        copyFromEvent: 'Copy from another event',
        copyModal: {
          title: 'Copy song list',
          description: 'Select an event to copy its song list to this event.',
          noEvents: 'No other events available',
        },
        copySuccess: 'Songs copied successfully',
        copySuccessCount: 'song(s) added',
        copySkipped: 'already present',
        copyNoSongs: 'The selected event contains no songs',
        copyAllAlreadyAdded: 'All songs from this event are already present',
        copyError: 'Error copying songs',
      },
    },
    songs: {
      title: 'Song Library',
      subtitle: 'Manage your song repertoire',
      songsList: 'Songs list',
      noSongs: 'No songs yet',
      addSong: 'Add song',
      add: 'Add',
      adding: 'Adding...',
      edit: 'Edit',
      editing: 'Editing...',
      cancel: 'Cancel',
      confirm: 'Confirm',
      confirmDeleteTitle: 'Delete song',
      confirmDelete: 'Are you sure you want to delete "{{title}}" by {{artist}}?',
      songTitle: 'Title',
      titlePlaceholder: 'Ex: Bohemian Rhapsody',
      artist: 'Artist',
      artistPlaceholder: 'Ex: Queen',
      duration: 'Duration',
      durationPlaceholder: 'Ex: 5:55',
      durationFormat: 'Format: MM:SS (ex: 3:45)',
      year: 'Year',
      yearPlaceholder: 'Ex: 2020',
      addSuccess: 'Song added successfully',
      addError: 'Error adding song',
      updateSuccess: 'Song updated successfully',
      updateError: 'Error updating song',
      deleteSuccess: 'Song deleted successfully',
      deleteError: 'Error deleting song',
      errorLoad: 'Error loading songs',
      delete: 'Delete',
    },
    settings: {
      title: 'Band Settings',
      subtitle: 'Manage your band information',
      bandName: 'Band name',
      bandNamePlaceholder: 'Your band name',
      logoUrl: 'Logo URL',
      logoUrlPlaceholder: 'https://example.com/logo.png',
      logoPreview: 'Logo preview',
      save: 'Save',
      saving: 'Saving...',
      successMessage: 'Settings saved successfully',
      errorLoad: 'Error loading settings',
      errorSave: 'Error saving settings',
    },
    members: {
      title: 'Band Members',
      subtitle: 'Manage your band members',
      membersList: 'Members list',
      noMembers: 'No members yet',
      inviteMember: 'Invite member',
      invite: 'Invite',
      inviting: 'Inviting...',
      cancel: 'Cancel',
      confirm: 'Confirm',
      confirmRemoveTitle: 'Remove member',
      confirmToggleOwnerTitle: 'Change owner status',
      email: 'Email',
      emailPlaceholder: 'member@example.com',
      password: 'Password',
      passwordPlaceholder: 'Minimum 8 characters',
      firstName: 'First name',
      firstNamePlaceholder: 'First name (optional)',
      lastName: 'Last name',
      lastNamePlaceholder: 'Last name (optional)',
      owner: 'Owner',
      invitedBy: 'Invited by',
      remove: 'Remove',
      confirmRemove: 'Are you sure you want to remove {{email}} from the band?',
      inviteSuccess: 'Member invited successfully',
      inviteError: 'Error inviting member',
      removeSuccess: 'Member removed successfully',
      removeError: 'Error removing member',
      errorLoad: 'Error loading members',
      promote: 'Promote to owner',
      demote: 'Demote',
      confirmToggleOwner: 'Are you sure you want to {{action}} this member?',
      promoteSuccess: 'Member promoted to owner successfully',
      demoteSuccess: 'Member demoted successfully',
      toggleOwnerError: 'Error changing member status',
    },
    venues: {
      title: 'Concert Venues',
      subtitle: 'Manage the venues where you perform',
      venuesList: 'Venues list',
      noVenues: 'No venues yet',
      addVenue: 'Add venue',
      add: 'Add',
      adding: 'Adding...',
      edit: 'Edit',
      editing: 'Editing...',
      cancel: 'Cancel',
      confirm: 'Confirm',
      confirmDeleteTitle: 'Delete venue',
      confirmDelete: 'Are you sure you want to delete the venue "{{name}}" ?',
      name: 'Venue name',
      namePlaceholder: 'Ex: The Zenith',
      address: 'Address',
      addressPlaceholder: 'Ex: 211 Avenue Jean Jaurès',
      city: 'City',
      cityPlaceholder: 'Ex: Paris',
      country: 'Country',
      countryPlaceholder: 'Ex: France',
      googleMapsUrl: 'Google Maps link',
      googleMapsUrlPlaceholder: 'Ex: https://maps.google.com/...',
      generateMapsUrl: 'Generate Google Maps link',
      generateMapsUrlError: 'Enter at least the venue name or city to generate the link.',
      viewOnGoogleMaps: 'View on Google Maps',
      addSuccess: 'Venue added successfully',
      addError: 'Error adding venue',
      updateSuccess: 'Venue updated successfully',
      updateError: 'Error updating venue',
      deleteSuccess: 'Venue deleted successfully',
      deleteError: 'Error deleting venue',
      errorLoad: 'Error loading venues',
    },
  },
  notFound: {
    title: 'Page not found',
    subtitle: 'Oops! This page does not exist',
    description: 'The page you are looking for may have been moved or deleted.',
    quickLinks: 'Quick links',
    goHome: 'Go to home',
    goToLogin: 'Go to login',
    helpText: 'If you think this is an error, please contact support.',
  },
}
